export const iamAdminZh = {
  title: '安全治理',
  nav: {
    overview: '总览',
    dataScopes: '数据范围',
    roleDataScopes: '角色 × 资源',
    fieldPermissions: '字段权限',
    emergencyBypass: '紧急豁免',
    auditLogs: '审计日志',
    help: '使用说明',
  },
  common: {
    loading: '加载中…',
    empty: '暂无数据',
    loadFailed: '加载失败',
    saved: '已保存',
    saveFailed: '保存失败',
    deleted: '已删除',
    deleteFailed: '删除失败',
    submitting: '提交中…',
    confirm: '确认',
    cancel: '取消',
    fillRequired: '请填写所有必填字段',
    actions: '操作',
    all: '全部',
    pickRole: '选择角色',
    pickResource: '选择 resource',
    pickScopeType: '选择 scopeType（CUSTOM 永久禁用）',
  },
  scopeType: {
    SELF: { label: 'SELF', hint: '仅本人创建的记录' },
    DEPARTMENT: { label: 'DEPARTMENT', hint: '本人所在部门（不含子部门）' },
    DEPARTMENT_TREE: { label: 'DEPARTMENT_TREE', hint: '本人所在部门 + 所有子部门' },
    ORGANIZATION: { label: 'ORGANIZATION', hint: '本人所在组织 + 全局记录' },
    REGION: { label: 'REGION', hint: '当前 currentRegion 上下文' },
    ALL: { label: 'ALL', hint: '全部数据（最宽）' },
  },
  fieldAccess: {
    VISIBLE: '可见可编辑（默认）',
    READONLY: '可见不可编辑',
    HIDDEN: '完全不可见',
    DESENSITIZE: '脱敏显示',
  },
  overview: {
    subtitle: '运维与合规视角的 IAM 治理：紧急豁免 + 审计日志',
    totalCount: '总数',
    activeCount: '生效中',
    dataScopesDesc: '管理数据范围的定义（SELF / DEPARTMENT / ORGANIZATION 等）',
    roleDataScopesDesc: '把角色 × 资源 绑定到指定的数据范围',
    fieldPermissionsDesc: '配置某角色对某资源某字段的可见 / 脱敏 / 隐藏',
    emergencyBypassDesc: '生产应急：4h 内对指定 endpoint 临时跳过权限校验',
    auditLogsDesc: '所有 IAM 配置变更与 admin bypass 的审计记录',
    rbacConfigTitle: 'RBAC 配置入口',
    rbacConfigDesc: '角色权限、数据范围绑定、字段权限请到「组织管理」配置',
    openRoles: '角色管理',
    openDataScopes: '数据范围目录',
  },
  dataScopes: {
    title: '数据范围定义',
    subtitle: '内置 DataScope 不可删除；CUSTOM 永久禁用',
    newButton: '新建',
    editTitle: '编辑数据范围',
    builtIn: '内置',
    custom: '自定义',
    builtInImmutable: '内置 DataScope 不允许删除',
    confirmDelete: '确认删除 {code}？',
    codeHint: '只允许大写字母 / 数字 / 下划线。建好后无法修改',
    columns: {
      code: '代码',
      name: '名称',
      scopeType: '范围类型',
      builtIn: '类型',
      actions: '操作',
    },
  },
  roleDataScopes: {
    title: '角色 × 资源 → 数据范围',
    subtitle: '同一角色对不同资源可绑定不同 DataScope；resource = * 是默认兜底',
    bindButton: '新建绑定',
    bindTitle: '绑定数据范围',
    pickRole: '选择角色',
    pickScope: '选择 DataScope',
    confirmUnbind: '确认解除该绑定？该角色对此资源将退回兜底逻辑',
    manageCatalog: '管理 DataScope 定义 →',
    columns: {
      resource: '资源',
      dataScope: '数据范围',
      scopeType: '范围类型',
    },
  },
  fieldPermissions: {
    title: '字段级权限',
    subtitle: '控制某角色对某资源某字段的访问级别。多角色取最宽松。',
    newButton: '新建',
    editTitle: '编辑字段权限',
    confirmDelete: '确认删除该字段权限规则？',
    filterRole: '按角色筛选',
    filterResource: '按资源筛选',
    filterResourcePlaceholder: '例：purchase_order',
    columns: {
      role: '角色',
      resource: '资源',
      field: '字段',
      access: '访问级别',
    },
  },
  emergencyBypass: {
    title: '紧急豁免',
    subtitle: '生产事故应急通道。豁免最长 4 小时，超时自动失效。',
    warning: '⚠️ 启用豁免会让指定 endpoint 跳过权限校验，仅用于阻塞级故障。每次启用都会进入审计日志。',
    newButton: '启用豁免',
    confirmEnable: '启用',
    dialogDesc: '启用后该 endpoint 在 TTL 内对所有用户跳过 PermissionsGuard。',
    confirmDisable: '解除对 {endpoint} 的豁免？',
    empty: '当前无生效豁免',
    ttlLabel: '有效时长（分钟）',
    ttlHint: '最长 240 分钟（4 小时）',
    ttlMaxError: '有效时长不能超过 240 分钟',
    endpointHint: '格式 "METHOD /path"，例如 "POST /api/v1/users"',
    columns: {
      endpoint: 'Endpoint',
      reason: '原因',
      enabledAt: '启用时间',
      expiresAt: '过期时间',
    },
  },
  auditLogs: {
    title: 'IAM 审计日志',
    subtitle: 'DataScope / RoleDataScope / FieldPermission / 紧急豁免 / Administrator bypass 的所有变更',
    search: '查询',
    viewDiff: '查看 diff',
    diffTitle: '变更详情',
    diffBefore: '操作前',
    diffAfter: '操作后',
    totalLabel: '总数',
    prev: '上一页',
    next: '下一页',
    filters: {
      actor: 'actor (UUID)',
      action: 'action',
      resource: 'resource',
    },
    columns: {
      timestamp: '时间',
      action: '操作',
      resource: '资源',
      actor: '操作者',
      targetId: '目标 ID',
    },
  },

  help: {
    title: '使用说明',
    subtitle: '快速了解 IAM 治理后台',
    intro: {
      title: '这是什么系统',
      paragraph:
        'IAM 治理后台是高级管理员的统一入口，用于配置数据可见范围（DataScope）、字段级脱敏、紧急豁免、委托、复核，并通过 IAM 审计日志保证所有 admin 操作可追溯。部分能力（DataScope / 字段权限管理 UI）仍在规划中，已落地的能力是紧急豁免和 IAM 审计日志查询。',
      highlights: [
        { title: '权限治理 UI', desc: '把原本要跑 SQL / Redis CLI 的运维动作收口到 UI' },
        { title: '专属审计', desc: 'iam_audit_log 独立记录 admin 治理动作，与业务审计分离' },
        { title: '紧急豁免', desc: '带 TTL 的临时后门，应急场景下解锁卡死流程' },
      ],
    },
    concepts: {
      title: '核心概念',
      description: 'IAM 四层模型与关键术语',
      items: [
        { term: 'IAM 四层', desc: 'L1 Role/Permission（谁能调）/ L2 OrganizationScope（哪个组织）/ L3 DataScope（哪些行）/ L4 FieldPermission（哪些字段）' },
        { term: 'DataScope', desc: '数据可见范围。内置：ALL / ORGANIZATION / DEPARTMENT_TREE（本部门+下级）/ SELF；CUSTOM 永久未实现' },
        { term: '角色 × 资源 绑定', desc: '同一角色对不同资源可绑不同 DataScope（如 Sales 对 Order 用 DEPARTMENT_TREE，对 Customer 用 ORGANIZATION）' },
        { term: 'FieldPermission', desc: '字段级访问规则；accessLevel = READ / READ_MASKED（脱敏）/ HIDDEN' },
        { term: 'EmergencyBypass', desc: '紧急豁免：给某 endpoint 临时开后门，带 TTL，全程审计；TTL 上限通常 24h' },
        { term: '委托（Delegation）', desc: '员工请假期间把权限临时委托给同事' },
        { term: 'Access Review', desc: '周期性复核（建议季度）：审视谁有哪些角色，确认是否仍需要，避免权限蠕变' },
        { term: 'iam_audit_log', desc: 'IAM 治理操作专用审计表，与业务 audit_log 独立；记录 DataScope 改动 / 豁免启用等' },
      ],
    },
    roles: {
      title: '角色与权限',
      description: '本模块面向高级管理员',
      columnHeaders: { role: '角色', scope: '可操作', desc: '说明' },
      items: [
        { code: 'Administrator', name: '系统管理员', scope: '全部', desc: 'IAM 治理后台所有功能' },
        { code: 'SecurityAdmin', name: '安全 / SOX 负责人', scope: '全部', desc: '同上；复核与审计查询为日常' },
        { code: 'Operations', name: 'IT 运维', scope: '紧急豁免 + 审计查询', desc: '应急处置场景使用' },
        { code: '一般业务用户', name: '业务用户', scope: '仅自己的委托', desc: '在 organization/delegations 看本人委托记录' },
      ],
    },
    tasks: {
      title: '常用任务',
      description: '当前可用的入口（规划中的页面后续接入）',
      cards: [
        { id: 'overview', name: '总览', desc: '治理动作概览、入口卡片' },
        { id: 'emergencyBypass', name: '紧急豁免', desc: '查看 active 豁免、新建带 TTL 的豁免、立即解除' },
        { id: 'auditLogs', name: 'IAM 审计日志', desc: '按 actor / action / resource / 时间范围检索治理动作' },
      ],
    },
  },
};
